// UX-1 — shared controlled-switch confirmation for Project Operations
// activation flags. Mandatory reason; when turning OFF it fetches the
// disable-impact (record counts + in-progress blockers) and blocks unsafe
// disables. Used by the Control Panel and the Projects page quick toggle.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

const IMPACT_LABELS = {
  boq_versions: "BOQ versions", prs: "PRs", pos: "POs", grns: "GRNs",
  pi_links: "PI linkage", pmc: "Material Consumption",
  work_progress: "Work Progress", labour_assignments: "Labour Assignments",
};

export function OpsSwitchConfirm({ title, turningOff, impactParams, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!turningOff) return;
    api.get("/project-ops/disable-impact", { params: impactParams })
      .then(r => setImpact(r.data)).catch(() => setImpact(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const blocked = turningOff && impact && !impact.can_disable;
  const submit = async () => {
    if (!reason.trim()) { toast.error("A reason is mandatory for every control change"); return; }
    setBusy(true);
    try { await onConfirm(reason.trim()); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center py-16 overflow-y-auto" data-testid="ops-switch-confirm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-5 space-y-3 text-xs">
        <div className="flex items-center justify-between">
          <div className="text-sm font-bold text-slate-900">{title}</div>
          <button data-testid="ops-confirm-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        {turningOff && (
          <div className="border border-amber-200 bg-amber-50/70 rounded p-2 space-y-1" data-testid="ops-impact-panel">
            <div className="font-bold text-amber-900">Safe disabling — existing records</div>
            {!impact ? <div className="text-slate-500">Calculating impact…</div> : (
              <>
                <div className="grid grid-cols-4 gap-1">
                  {Object.entries(impact.counts).map(([k, v]) => (
                    <div key={k} className="bg-white border border-amber-100 rounded px-1.5 py-1">
                      <div className="text-[9px] uppercase text-slate-400 font-bold">{IMPACT_LABELS[k] || k}</div>
                      <div className="font-bold tabular-nums" data-testid={`ops-impact-${k}`}>{v}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] text-slate-600">{impact.note}</div>
                {impact.blockers?.length > 0 && (
                  <div className="text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="ops-impact-blockers">
                    <b>Disabling is blocked</b> — in-progress operations must complete or be resolved first:
                    <ul className="list-disc ml-4">{impact.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        <label className="block">Reason (mandatory, recorded in the append-only audit)
          <textarea data-testid="ops-confirm-reason" className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 h-16"
            value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this switch being changed?" />
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 font-bold text-slate-600 border border-slate-300 rounded hover:bg-slate-50">Cancel</button>
          <button data-testid="ops-confirm-apply" disabled={busy || blocked || (turningOff && !impact)} onClick={submit}
            className={`px-4 py-1.5 font-bold text-white rounded disabled:opacity-50 ${turningOff ? "bg-rose-700 hover:bg-rose-800" : "bg-[#0B1F3A] hover:bg-[#13294f]"}`}>
            {busy ? "Applying…" : blocked ? "Blocked" : turningOff ? "Switch OFF" : "Switch ON"}
          </button>
        </div>
      </div>
    </div>
  );
}
